import { useMemo } from 'react';
import type { Rally, ResultsResponse } from '@/api/resultsApi';
import { useResultsPolling } from './useResultsPolling';

interface UseMatchDataReturn {
  results: ResultsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  playerTeams: Record<number, 'A' | 'B'>;
  heatmapPoints: { x: number; y: number; confidence: number }[];
}

function inferTeams(rallies: Rally[]): Record<number, 'A' | 'B'> {
  const counts: Record<number, Record<string, number>> = {};
  for (const r of rallies) {
    for (const ev of r.events) {
      if (!counts[ev.player_id]) counts[ev.player_id] = { A: 0, B: 0 };
      if (ev.team === 'A' || ev.team === 'B') {
        counts[ev.player_id][ev.team]++;
      }
    }
  }
  const teams: Record<number, 'A' | 'B'> = {};
  for (const [pid, cnt] of Object.entries(counts)) {
    teams[Number(pid)] = cnt.A >= cnt.B ? 'A' : 'B';
  }
  return teams;
}

function getHeatmapPoints(rallies: Rally[]): { x: number; y: number; confidence: number }[] {
  const points: { x: number; y: number; confidence: number }[] = [];
  for (const r of rallies) {
    for (const ev of r.events) {
      if (ev.type === 'attack' || ev.type === 'tip') {
        points.push({
          x: ev.spatial_location.x,
          y: ev.spatial_location.y,
          confidence: ev.confidence,
        });
      }
    }
  }
  return points;
}

export function useMatchData(videoId: string | null): UseMatchDataReturn {
  const { data, isLoading, error, refetch } = useResultsPolling(videoId);

  const playerTeams = useMemo(
    () => (data?.rallies ? inferTeams(data.rallies) : {}),
    [data]
  );

  const heatmapPoints = useMemo(
    () => (data?.rallies ? getHeatmapPoints(data.rallies) : []),
    [data]
  );

  return {
    results: data,
    isLoading,
    error,
    refetch,
    playerTeams,
    heatmapPoints,
  };
}